import React, {
    useCallback, useContext, useEffect, useState
} from 'react';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import CommonConstants from 'sg-common-constants';
import _ from 'lodash';
import {
    useSgHandleSubQuestions, SgSessionStorageService, SgGdprUtilService, SgCpcUtilService
} from 'saga-modules';
import messages from './SgNameDetails.messages';
import SgNameDetailsMetadata from './SgNameDetails.metadata.json5';

function SgNameDetails(props) {
    const {
        id,
        modelVM,
        updateData,
        showErrors,
        path,
        messageParams,
        readOnlyName,
        overrideTitleLabel,
        hideGender,
        preventClearingGender,
        suffix,
        onValidate: onValidateParent,
        isPolicyHolder,
        shouldCallNewCustomerPermission
    } = props;
    const {
        isComponentValid,
        onValidate
    } = useValidation(id);

    const translator = useContext(TranslatorContext);
    const [invalidSubQuestions, updateInvalidSubQuestions] = useState(
        SgSessionStorageService.getItem(SgSessionStorageService.KEY.INVALID_SUB_QUESTIONS)
    );
    const prefixModel = _.get(modelVM, `${path}.prefix`);
    const genderModel = _.get(modelVM, `${path}.gender`);
    const firstName = _.get(modelVM, `${path}.firstName.value`);
    const lastName = _.get(modelVM, `${path}.lastName.value`);
    const [showGender, setShowGender] = useState(prefixModel.value && prefixModel.value.code === CommonConstants.TITLE_DR);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        if (prefixModel && prefixModel.value && prefixModel.value.code !== CommonConstants.TITLE_DR && genderModel) {
            setShowGender(false);
            if (prefixModel.value.code === CommonConstants.TITLE_MR) {
                _.set(modelVM, `${path}.gender`, CommonConstants.GENDER_MALE);
            } else {
                _.set(modelVM, `${path}.gender`, CommonConstants.GENDER_FEMALE);
            }
        } else if (prefixModel && prefixModel.value && prefixModel.value.code === CommonConstants.TITLE_DR && !showGender) {
            setShowGender(true);
            _.set(modelVM, `${path}.gender`, undefined);
        }
        updateData(modelVM);
    }, [prefixModel.value]);

    useSgHandleSubQuestions({
        updateModel: updateData,
        updateInvalidSubQuestions,
        modelVM,
        paths: [
            `${path}.prefix`,
            `${path}.firstName`,
            `${path}.lastName`,
            {
                path: `${path}.gender`,
                preventClearing: preventClearingGender,
                visible: !hideGender && showGender === true
            }
        ]
    });

    const getGdprData = useCallback(() => {
        if (isPolicyHolder) {
            if (shouldCallNewCustomerPermission) {
                SgCpcUtilService.getCpcData(modelVM);
            } else {
                SgGdprUtilService.getGdprData(modelVM, updateData, false);
            }
        }
    }, [isPolicyHolder, modelVM, updateData]);

    const callGdprOnValueChange = useCallback((val, selectedChange) => {
        if (selectedChange === CommonConstants.TITLE) {
            _.set(modelVM, `${path}.prefix`, val);
        } else {
            _.set(modelVM, `${path}.gender`, val);
        }
        updateData(modelVM);
        if (isPolicyHolder && shouldCallNewCustomerPermission) {
            SgCpcUtilService.getCpcData(modelVM);
        }
    }, [modelVM, updateData]);

    const overrideProps = {
        title: {
            id: `title${suffix}`,
            label: overrideTitleLabel || translator(messages.title, messageParams),
            path: `${path}.prefix`,
            onValueChange: (value) => callGdprOnValueChange(value, CommonConstants.TITLE),
            showErrors: invalidSubQuestions.includes(`${path}.prefix`)
        },
        nameInput: {
            id: `nameInput${suffix}`,
            label: translator(messages.firstName, messageParams),
            path: `${path}.firstName`,
            onBlur: getGdprData,
            visible: !readOnlyName,
            showErrors: invalidSubQuestions.includes(`${path}.firstName`)
        },
        surnameInput: {
            id: `surnameInput${suffix}`,
            label: translator(messages.lastName, messageParams),
            path: `${path}.lastName`,
            onBlur: getGdprData,
            visible: !readOnlyName,
            showErrors: invalidSubQuestions.includes(`${path}.lastName`)
        },
        gender: {
            id: `gender${suffix}`,
            visible: !hideGender && showGender === true,
            path: `${path}.gender`,
            showErrors: invalidSubQuestions.includes(`${path}.gender`) && !hideGender && showGender === true,
            onValueChange: (value) => callGdprOnValueChange(value, CommonConstants.GENDER),
        },
        readOnlyNameContainer: {
            visible: readOnlyName ? '' : false
        },
        readOnlyNameValue: {
            content: `${firstName} ${lastName}`
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate
        },
    };

    return (
        <ViewModelForm
            uiProps={SgNameDetailsMetadata.componentContent}
            model={modelVM}
            showErrors={showErrors}
            onModelChange={updateData}
            onValidationChange={onValidate}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

SgNameDetails.defaultProps = {
    suffix: 'PolicyHolder',
    readOnlyName: false
};

export default SgNameDetails;
